import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import {
  useAnnotateMtoAnomalyMutation,
  useDecideMtoRunAnomaliesMutation,
} from '@shared/api';
import type { MtoAnomaly } from '@entities/mto';
import {
  anomalyKindLabel,
  apiErrorMessage,
  decisionLabel,
  decisionTone,
  severityLabel,
  severityTone,
} from '@entities/mto';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { IconChevronDown, IconChevronRight } from '@shared/ui/icons';
import type { AnomalyGroupData, BulkAction } from '../types';
import {
  ANOMALIES_FLAT_LIMIT,
  GROUP_COLLAPSE_AT,
  GROUP_PAGE,
  anomalyRank,
  bulkQuestion,
  groupAnomalies,
} from '../helpers';
import {
  Actions,
  AnomalyCard,
  AnomalyDesc,
  AnomalyHead,
  AnomalyKind,
  AnomalyStack,
  AnomalyTitle,
  BulkBar,
  Empty,
  ErrBox,
  Group,
  GroupActions,
  GroupHead,
  GroupMeta,
  Groups,
  GroupToggle,
  RowActions,
} from '../styles';

/** Одна аномалия с HITL (учесть / отклонить). */
export function AnomalyItem({
  anomaly,
  runId,
  canWrite,
  onError,
}: {
  anomaly: MtoAnomaly;
  runId: string;
  canWrite: boolean;
  onError: (msg: string) => void;
}) {
  const [annotate, { isLoading }] = useAnnotateMtoAnomalyMutation();
  const decided = Boolean(anomaly.decision);
  const isCritical = anomaly.severity === 'critical';

  async function handleDecide(decision: 'acknowledged' | 'rejected') {
    onError('');
    try {
      await annotate({ id: anomaly.id, runId, decision }).unwrap();
    } catch (err) {
      onError(apiErrorMessage(err) ?? 'Не удалось сохранить решение по расхождению.');
      // eslint-disable-next-line no-console
      console.error('annotateMtoAnomaly failed', err);
    }
  }

  return (
    <AnomalyCard $critical={isCritical && !decided}>
      <AnomalyHead>
        <div>
          {anomalyKindLabel(anomaly.kind) && (
            <AnomalyKind>{anomalyKindLabel(anomaly.kind)}</AnomalyKind>
          )}
          <AnomalyTitle>{anomaly.title}</AnomalyTitle>
          {anomaly.description && <AnomalyDesc>{anomaly.description}</AnomalyDesc>}
        </div>
        <RowActions>
          {anomaly.provisional && <Badge $tone="neutral">не подтверждено</Badge>}
          <Badge $tone={severityTone(anomaly.severity)}>{severityLabel(anomaly.severity)}</Badge>
        </RowActions>
      </AnomalyHead>

      {decided ? (
        <Actions>
          <Badge $tone={decisionTone(anomaly.decision ?? '')}>
            {decisionLabel(anomaly.decision ?? '')}
          </Badge>
        </Actions>
      ) : (
        <Actions>
          <Button
            type="button"
            $variant="secondary"
            $size="sm"
            disabled={isLoading || !canWrite}
            title={!canWrite ? 'Только просмотр' : undefined}
            onClick={() => handleDecide('acknowledged')}
          >
            Учесть
          </Button>
          <Button
            type="button"
            $variant="ghost"
            $size="sm"
            disabled={isLoading || !canWrite}
            title={!canWrite ? 'Только просмотр' : undefined}
            onClick={() => handleDecide('rejected')}
          >
            Отклонить
          </Button>
        </Actions>
      )}
    </AnomalyCard>
  );
}

/** Подтверждение массового решения — там же, где нажали. */
export function BulkConfirm({
  pending,
  busy,
  confirmRef,
  onConfirm,
  onCancel,
}: {
  pending: BulkAction;
  busy: boolean;
  confirmRef: RefObject<HTMLButtonElement | null>;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <BulkBar role="group" aria-label="Подтверждение массового решения">
      <span>{bulkQuestion(pending)}</span>
      <Button
        ref={confirmRef}
        type="button"
        $variant="secondary"
        $size="sm"
        disabled={busy}
        onClick={onConfirm}
      >
        {busy ? 'Сохраняю…' : pending.decision === 'acknowledged' ? 'Да, учесть' : 'Да, отклонить'}
      </Button>
      <Button type="button" $variant="ghost" $size="sm" disabled={busy} onClick={onCancel}>
        Отмена
      </Button>
    </BulkBar>
  );
}

/** Группа замечаний одного вида: заголовок с числом и важностью, «учесть /
 *  отклонить все этого вида», список — постранично. */
export function AnomalyGroup({
  group,
  runId,
  canWrite,
  confirm,
  onAsk,
  onError,
}: {
  group: AnomalyGroupData;
  runId: string;
  canWrite: boolean;
  /** Подтверждение, если массовое решение спросили именно в этой группе. */
  confirm: ReactNode;
  onAsk: (a: BulkAction) => void;
  onError: (msg: string) => void;
}) {
  const { kind, items, undecided, worst } = group;
  const critical = items.some((a) => a.severity === 'critical' && !a.decision);
  const [open, setOpen] = useState(critical || items.length < GROUP_COLLAPSE_AT);
  const [shown, setShown] = useState(GROUP_PAGE);
  const label = anomalyKindLabel(kind) || 'Прочие замечания';

  return (
    <Group>
      <GroupHead>
        <GroupToggle type="button" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          {open ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
          <span>{label}</span>
          <Badge $tone="neutral">{items.length}</Badge>
          <Badge $tone={severityTone(worst)}>{severityLabel(worst)}</Badge>
        </GroupToggle>
        <GroupMeta>
          {undecided === 0
            ? 'все разобраны'
            : undecided === items.length
              ? 'не разобраны'
              : `не разобрано: ${undecided}`}
        </GroupMeta>
        {canWrite && undecided > 1 && !confirm && (
          <GroupActions>
            <Button
              type="button"
              $variant="secondary"
              $size="sm"
              aria-label={`Учесть все: ${label}`}
              onClick={() => onAsk({ decision: 'acknowledged', kind, count: undecided })}
            >
              Учесть все
            </Button>
            <Button
              type="button"
              $variant="ghost"
              $size="sm"
              aria-label={`Отклонить все: ${label}`}
              onClick={() => onAsk({ decision: 'rejected', kind, count: undecided })}
            >
              Отклонить все
            </Button>
          </GroupActions>
        )}
      </GroupHead>
      {confirm}
      {open && (
        <AnomalyStack>
          {items.slice(0, shown).map((a) => (
            <AnomalyItem
              key={a.id}
              anomaly={a}
              runId={runId}
              canWrite={canWrite}
              onError={onError}
            />
          ))}
          {items.length > shown && (
            <div>
              <Button
                type="button"
                $variant="secondary"
                $size="sm"
                onClick={() => setShown((n) => n + GROUP_PAGE)}
              >
                Показать ещё {Math.min(GROUP_PAGE, items.length - shown)}
              </Button>
            </div>
          )}
        </AnomalyStack>
      )}
    </Group>
  );
}

/** Блок замечаний: неразобранные и важные наверх; когда их много — группы по
 *  видам. Массовые «учесть / отклонить» (по всем или по виду) — с
 *  подтверждением рядом с кнопкой; ошибки — здесь же, а не в шапке карточки. */
export function AnomaliesSection({
  anomalies,
  runId,
  canWrite,
}: {
  anomalies: MtoAnomaly[];
  runId: string;
  canWrite: boolean;
}) {
  const [bulk, { isLoading: bulking }] = useDecideMtoRunAnomaliesMutation();
  const [pending, setPending] = useState<BulkAction | null>(null);
  const [error, setError] = useState('');
  const confirmRef = useRef<HTMLButtonElement>(null);
  const groups = useMemo(() => groupAnomalies(anomalies), [anomalies]);

  // Кнопка, по которой нажали, исчезает — фокус переводим на подтверждение.
  useEffect(() => {
    if (pending) confirmRef.current?.focus();
  }, [pending]);

  if (anomalies.length === 0) {
    return <Empty>Замечаний нет.</Empty>;
  }
  const open = anomalies.filter((a) => !a.decision).length;

  async function confirmBulk() {
    if (!pending) return;
    setError('');
    try {
      await bulk({
        id: runId,
        decision: pending.decision,
        kinds: pending.kind ? [pending.kind] : undefined,
      }).unwrap();
      setPending(null);
    } catch (err) {
      setError(apiErrorMessage(err) ?? 'Не удалось сохранить решения по замечаниям.');
    }
  }

  const confirmFor = (kind: string | undefined) =>
    pending && pending.kind === kind ? (
      <BulkConfirm
        pending={pending}
        busy={bulking}
        confirmRef={confirmRef}
        onConfirm={confirmBulk}
        onCancel={() => setPending(null)}
      />
    ) : null;

  return (
    <>
      {canWrite &&
        open > 1 &&
        (confirmFor(undefined) ?? (
          <BulkBar>
            <span>
              Не разобрано: {open} из {anomalies.length}.
            </span>
            <Button
              type="button"
              $variant="secondary"
              $size="sm"
              onClick={() => setPending({ decision: 'acknowledged', count: open })}
            >
              Учесть все
            </Button>
            <Button
              type="button"
              $variant="ghost"
              $size="sm"
              onClick={() => setPending({ decision: 'rejected', count: open })}
            >
              Отклонить все
            </Button>
          </BulkBar>
        ))}
      {error && <ErrBox role="alert">{error}</ErrBox>}
      {anomalies.length <= ANOMALIES_FLAT_LIMIT ? (
        <AnomalyStack>
          {groups
            .flatMap((g) => g.items)
            .sort((a, b) => anomalyRank(b) - anomalyRank(a))
            .map((a) => (
              <AnomalyItem
                key={a.id}
                anomaly={a}
                runId={runId}
                canWrite={canWrite}
                onError={setError}
              />
            ))}
        </AnomalyStack>
      ) : (
        <Groups>
          {groups.map((g) => (
            <AnomalyGroup
              key={g.kind}
              group={g}
              runId={runId}
              canWrite={canWrite}
              confirm={confirmFor(g.kind)}
              onAsk={setPending}
              onError={setError}
            />
          ))}
        </Groups>
      )}
    </>
  );
}
